import type { FC } from 'react';
import { useEffect, useMemo } from 'react';

import { Outlet, useNavigate, useSearchParams } from 'react-router';
import { FeatureFlag } from '@/services/feature-flags';
import { useFlag } from '@/services/feature-flags/react';
import { Box, Container } from '@/shared/ui';

import { ExpiredBanner } from '@/app/components/ExpiredBanner';
import { ImpersonatorBanner } from '@/app/components/ImpersonatorBanner';
import LinkBar from '@/app/components/LinkBar';
import { NavigationMenu } from '@/app/components/NavigationMenu';
import { VersionUpdateBanner } from '@/app/components/VersionUpdateBanner';
import { Permission } from '@/entities/permissions';
import UpgradeToPremiumButton from '@/features/_shared/UpgradeToPremium/UpgradeToPremiumButton';
import { usePermission, useUserDetails } from '@/features/_shared/user';
import { AskGXButton, AskGXDrawer } from '@/features/ai-chat';
import { useSavedFiltersSync } from '@/features/catalog/api/savedFilters';
import { useDashboardSync } from '@/features/dashboard/hooks';
import ExportChartButton from '@/features/data-hub/components/ExportChartButton';
import { ExplainerVideoModal, useExplainerVideoModal } from '@/features/explainer-video';
import FactsheetDialog from '@/features/factsheet/components/FactsheetDialog';
import { FACTSHEET_PARAM_NAME } from '@/features/factsheet/constants';
import { ExportChartButtonContext } from '@/features/factsheet/state/ExportChartButtonContext';
import type { ChartButtonRenderer } from '@/features/factsheet/state/ExportChartButtonContext';
import type { FactsheetSelectionState } from '@/features/factsheet/state/FactsheetSelectionContext';
import { useExpiredBannerContent } from '@/features/profile/hooks/useExpiredBannerContent';
import { useSelectionFunctions } from '@/features/symbol-selector';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { closeDrawer, DrawerType, openDrawer } from '@/store/slices/drawer.slice';

const renderExportChartButton: ChartButtonRenderer = graphType => (
  <ExportChartButton graphType={graphType} />
);

/**
 * Main application layout with navigation, banners, and content area.
 *
 * Features:
 * - Persistent navigation menu (sidebar)
 * - Version update banner
 * - Expired subscription banner
 * - Upgrade to premium button (for basic users)
 * - AI chat integration (feature-flagged)
 * - Factsheet dialog handling
 * - Terms acceptance check and redirect
 * - Responsive container with max-width constraints
 *
 * This layout wraps all authenticated application pages.
 */
export const AppLayout: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  useDashboardSync();
  useSavedFiltersSync();
  const tripTokenState = useAppSelector(state => state.tripToken);
  const openDrawerType = useAppSelector(state => state.drawer.openDrawer);
  const { isUserExpired, impersonatorEmail } = useUserDetails();
  const { hasPermission } = usePermission();
  const expiredBannerContent = useExpiredBannerContent();
  const showUpgradeButton = useMemo(
    () =>
      isUserExpired !== undefined &&
      !hasPermission(Permission.DashboardPremiumWidgets) &&
      !expiredBannerContent,
    [expiredBannerContent, hasPermission, isUserExpired]
  );
  const showAskGXButton = useFlag(FeatureFlag.AiChatEnabled) && !isUserExpired;
  const { isOpen: isExplainerVideoOpen, onDismiss: onExplainerVideoDismiss } =
    useExplainerVideoModal();
  const [searchParams] = useSearchParams();

  const factsheetCode = searchParams.get(FACTSHEET_PARAM_NAME);

  const {
    isSymbolSelected,
    toggleSymbolInSelection,
    addSymbolsToSelection,
    removeSymbolsFromSelection,
    areAllSymbolsSelected,
    isSpotLocked
  } = useSelectionFunctions();
  const factsheetSelectionState: FactsheetSelectionState = {
    isSymbolSelected,
    toggleSymbolInSelection,
    addSymbolsToSelection,
    removeSymbolsFromSelection,
    areAllSymbolsSelected,
    isSpotLocked
  };

  useEffect(() => {
    if (searchParams.get('prompt')) {
      dispatch(openDrawer(DrawerType.ASK_GX));
    }
  }, [searchParams, dispatch]);

  useEffect(() => {
    //When impersonator prevents T&C acceptance
    if (impersonatorEmail) {
      return;
    }
    if (tripTokenState.status === 'fulfilled' && !tripTokenState.terms) {
      navigate('/welcome');
    }
  }, [tripTokenState.status, tripTokenState.terms, impersonatorEmail, navigate]);

  return (
    <Container
      maxWidth='xl'
      sx={theme => ({
        display: 'flex',
        height: '100vh',
        width: '100%',
        pr: '0!important',
        pl: '0!important',
        overflow: 'hidden',
        [theme.breakpoints.up('xl')]: {
          borderLeft: '1px solid',
          borderLeftColor: 'gx.tileLight'
        }
      })}
    >
      <ExportChartButtonContext value={renderExportChartButton}>
        <FactsheetDialog
          code={factsheetCode || ''}
          open={factsheetCode !== null}
          selectionState={factsheetSelectionState}
        />
      </ExportChartButtonContext>
      <ExplainerVideoModal
        open={isExplainerVideoOpen}
        onClose={onExplainerVideoDismiss}
      />
      <NavigationMenu />
      <Box
        sx={{
          pt: 3,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minWidth: 0,
          height: '100vh',
          position: 'relative',
          boxSizing: 'border-box'
        }}
      >
        <VersionUpdateBanner />
        <ExpiredBanner />
        <Box
          sx={{
            px: 3,
            pb: 3,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          <ImpersonatorBanner />
          <Box sx={{ display: 'flex', gap: 3 }}>
            <LinkBar />
            {showUpgradeButton && (
              <UpgradeToPremiumButton
                size='large'
                sx={{ flexShrink: 0, whiteSpace: 'nowrap', fontSize: 16 }}
                type='upgrade'
              />
            )}
            {showAskGXButton && (
              <>
                <AskGXButton
                  onClick={() => {
                    dispatch(openDrawer(DrawerType.ASK_GX));
                  }}
                />
                <AskGXDrawer
                  open={openDrawerType === DrawerType.ASK_GX}
                  onClose={e => {
                    e.stopPropagation();
                    dispatch(closeDrawer());
                  }}
                />
              </>
            )}
          </Box>
          <Outlet />
        </Box>
      </Box>
    </Container>
  );
};
